/**
 * Notification Preferences Page
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bell, Mail, MessageSquare, Clock, Save } from 'lucide-react';

interface NotificationPrefs {
  emailNewTicketAssigned: boolean;
  emailTicketAssignedToMe: boolean;
  emailTicketReply: boolean;
  emailTicketResolved: boolean;
  emailSlaWarning: boolean;
  emailSlaBreach: boolean;
  smsNewTicket: boolean;
  smsSlaBreach: boolean;
  smsTicketAssigned: boolean;
  digestFrequency: string;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
}

const defaultPrefs: NotificationPrefs = {
  emailNewTicketAssigned: true,
  emailTicketAssignedToMe: true,
  emailTicketReply: true,
  emailTicketResolved: false,
  emailSlaWarning: true,
  emailSlaBreach: true,
  smsNewTicket: false,
  smsSlaBreach: true,
  smsTicketAssigned: false,
  digestFrequency: 'realtime',
  quietHoursStart: null,
  quietHoursEnd: null,
};

function ToggleButton({
  enabled,
  onToggle,
  label,
  description,
}: {
  enabled: boolean;
  onToggle: () => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={onToggle}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          enabled ? 'bg-primary' : 'bg-muted'
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
            enabled ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  );
}

export function NotificationPrefsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs);
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: async () => {
      const result = await api.getNotificationPrefs();
      return result;
    },
  });

  useEffect(() => {
    if (data?.data) {
      const loaded = data.data as Partial<NotificationPrefs>;
      setPrefs({
        ...defaultPrefs,
        ...loaded,
      });
      setHasChanges(false);
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (updatedPrefs: Record<string, unknown>) =>
      api.updateNotificationPrefs(updatedPrefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-prefs'] });
      setHasChanges(false);
      toast({ title: 'Notification preferences saved' });
    },
    onError: () => {
      toast({ title: 'Failed to save preferences', variant: 'destructive' });
    },
  });

  function updatePref<K extends keyof NotificationPrefs>(key: K, value: NotificationPrefs[K]) {
    setPrefs((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }

  function togglePref(key: keyof NotificationPrefs) {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
    setHasChanges(true);
  }

  function handleSave() {
    updateMutation.mutate(prefs as unknown as Record<string, unknown>);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6" />
          Notification Preferences
        </h1>
        <p className="text-muted-foreground">
          Configure how and when you receive notifications about support tickets.
        </p>
      </div>

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5 text-muted-foreground" />
            Email Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          <ToggleButton
            enabled={prefs.emailNewTicketAssigned}
            onToggle={() => togglePref('emailNewTicketAssigned')}
            label="New Ticket Assigned"
            description="Receive an email when a new ticket is assigned to your team"
          />
          <ToggleButton
            enabled={prefs.emailTicketAssignedToMe}
            onToggle={() => togglePref('emailTicketAssignedToMe')}
            label="Ticket Assigned to Me"
            description="Receive an email when a ticket is directly assigned to you"
          />
          <ToggleButton
            enabled={prefs.emailTicketReply}
            onToggle={() => togglePref('emailTicketReply')}
            label="Ticket Reply"
            description="Receive an email when a customer or agent replies to a ticket you're watching"
          />
          <ToggleButton
            enabled={prefs.emailTicketResolved}
            onToggle={() => togglePref('emailTicketResolved')}
            label="Ticket Resolved"
            description="Receive an email when a ticket you're watching is resolved"
          />
          <ToggleButton
            enabled={prefs.emailSlaWarning}
            onToggle={() => togglePref('emailSlaWarning')}
            label="SLA Warning"
            description="Receive an email when an SLA deadline is approaching"
          />
          <ToggleButton
            enabled={prefs.emailSlaBreach}
            onToggle={() => togglePref('emailSlaBreach')}
            label="SLA Breach"
            description="Receive an email when an SLA has been breached"
          />
        </CardContent>
      </Card>

      {/* SMS Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            SMS Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          <ToggleButton
            enabled={prefs.smsNewTicket}
            onToggle={() => togglePref('smsNewTicket')}
            label="New Ticket"
            description="Receive an SMS for new high-priority tickets"
          />
          <ToggleButton
            enabled={prefs.smsSlaBreach}
            onToggle={() => togglePref('smsSlaBreach')}
            label="SLA Breach"
            description="Receive an SMS when an SLA has been breached"
          />
          <ToggleButton
            enabled={prefs.smsTicketAssigned}
            onToggle={() => togglePref('smsTicketAssigned')}
            label="Ticket Assigned"
            description="Receive an SMS when a ticket is assigned to you"
          />
        </CardContent>
      </Card>

      {/* Digest Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Digest Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Notification Frequency</Label>
            <Select
              value={prefs.digestFrequency}
              onValueChange={(v) => updatePref('digestFrequency', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">Real-time</SelectItem>
                <SelectItem value="hourly">Hourly Digest</SelectItem>
                <SelectItem value="daily">Daily Digest</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {prefs.digestFrequency === 'realtime'
                ? 'You will receive notifications immediately as events occur.'
                : prefs.digestFrequency === 'hourly'
                ? 'Notifications will be batched and sent every hour.'
                : 'Notifications will be batched and sent once per day.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Quiet Hours Start</Label>
              <Input
                type="time"
                value={prefs.quietHoursStart || ''}
                onChange={(e) => updatePref('quietHoursStart', e.target.value || null)}
              />
            </div>
            <div>
              <Label>Quiet Hours End</Label>
              <Input
                type="time"
                value={prefs.quietHoursEnd || ''}
                onChange={(e) => updatePref('quietHoursEnd', e.target.value || null)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            During quiet hours, non-urgent notifications will be held and delivered when quiet
            hours end.
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          size="lg"
        >
          <Save className="h-4 w-4 mr-2" />
          {updateMutation.isPending ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
